import { queryOptions } from '@tanstack/react-query'

import { QuoteLinkError } from './quote'

// Public /public/invoices/{token} contract (USA-1283). Verified against the actual router by
// invoice-contract.test.ts; independent of dashboard contracts. Type names drop the API's `Qb` prefix.

const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:80'
const REQUEST_TIMEOUT_MS = 30_000

/** The manufacturer and model, then the line's specifications (or the text staff typed). */
export type PublicInvoiceLine = {
  manufacturer: string | null
  model: string | null
  description: string
  quantity: string | null
  amount: string
}

export type PublicInvoiceAddress = {
  name: string | null
  contact_name: string | null
  line1: string | null
  line2: string | null
  city: string | null
  state: string | null
  postal_code: string | null
  country: string | null
}

export type PublicInvoice = {
  number: string
  label: string
  title: string
  quote_number: string | null
  status: 'draft' | 'open' | 'paid' | 'uncollectible' | 'void'
  /** A bank payment was made and is clearing. */
  payment_processing: boolean
  payable: boolean
  issued_at: string | null
  due_at: string | null
  paid_at: string | null
  terms: string
  /** For a deposit or balance invoice, which share of the quote it bills. */
  installment: string | null
  customer_name: string
  contact_name: string | null
  bill_to: PublicInvoiceAddress | null
  ship_to: PublicInvoiceAddress | null
  /** Every item at its full price, then the quote's shipping, discount and tax. */
  items: PublicInvoiceLine[]
  subtotal: string
  shipping: string
  discount: string
  tax: string
  quote_total: string
  /** What this invoice bills: the quote total, or its deposit or balance share. */
  total: string
  credited: string
  amount_paid: string
  amount_remaining: string
  memo: string | null
  contact_email: string
  document_url: string
  /** Only an invoice Stripe issued before USA-1283: pay on Stripe's page. */
  stripe_pay_url: string | null
}

export type InvoiceCheckout = {
  url: string
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const controller = new AbortController()
  const timer = setTimeout(() => controller.abort(), REQUEST_TIMEOUT_MS)
  try {
    const response = await fetch(`${API_BASE_URL}${path}`, {
      ...init,
      signal: init?.signal ?? controller.signal,
      headers: { Accept: 'application/json' },
    })
    if (!response.ok) {
      let code = `request_failed_${response.status}`
      try {
        const body = (await response.json()) as { detail?: { code?: string } | string }
        code = (typeof body.detail === 'string' ? body.detail : body.detail?.code) || code
      } catch {
        // keep the status-based code
      }
      throw new QuoteLinkError(response.status, code)
    }
    return (await response.json()) as T
  } finally {
    clearTimeout(timer)
  }
}

const invoicePath = (token: string, suffix = '') =>
  `/public/invoices/${encodeURIComponent(token)}${suffix}`

export const invoiceQueryOptions = (token: string) =>
  queryOptions({
    queryKey: ['customer-invoice', token],
    queryFn: ({ signal }) => request<PublicInvoice>(invoicePath(token), { signal }),
    retry: (count, error) => !(error instanceof QuoteLinkError && error.status < 500) && count < 2,
  })

/** Stripe Checkout for what is left to pay; the page sends the customer there. */
export function startInvoiceCheckout(token: string) {
  return request<InvoiceCheckout>(invoicePath(token, '/pay'), { method: 'POST' })
}
